/**
 * F-507-FE-02: DisclaimerPage
 *
 * AI-assist disclosure at /session/:id/disclaimer.
 * Rendered in the patient's session language (Arabic RTL at pilot launch).
 *
 * Design: Single card of text, centered. One large "I Understand" button.
 * Cannot be skipped — the button is the only interactive element.
 *
 * On acknowledgement:
 * 1. Sends { type: 'DISCLAIMER_ACK' } over WebSocket
 * 2. Navigates to /session/:id/patient
 *
 * The server sets disclaimerAcknowledged=true in Redis session state on
 * receipt of DISCLAIMER_ACK (ARCH-009: VAD cannot activate until this flag).
 */

import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Localised strings (Arabic pilot)
// ---------------------------------------------------------------------------

const STRINGS = {
  title: 'معلومات مهمة',                                          // Important Information
  intro: 'قبل البدء، يرجى قراءة المعلومات التالية:',              // Before we begin, please read:
  points: [
    'هذه الأداة تساعد في الترجمة بينك وبين طبيبك.',               // This tool assists translation between you and your doctor.
    'تستخدم هذه الأداة تقنية الذكاء الاصطناعي للترجمة.',           // It uses AI technology for translation.
    'يتابع الطبيب الترجمة ويشرف عليها.',                           // The doctor monitors and oversees the translation.
    'لا يتم تسجيل أو حفظ كلامك.',                                  // Your speech is not recorded or saved.
  ],
  ackButton: 'فهمت',                                               // I Understand
  acknowledging: 'جارٍ التأكيد...',                                // Confirming...
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * SessionStorage key for the disclaimer acknowledgement flag.
 * PatientSessionPage reads this key and sends DISCLAIMER_ACK over the patient
 * WebSocket immediately after connecting — the patient's WebSocket is
 * established in PatientSessionPage (not here), so the ACK must be deferred.
 */
const DISCLAIMER_ACK_KEY = 'patient_disclaimer_pending';

export function DisclaimerPage() {
  const { id: sessionId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [isAcknowledging, setIsAcknowledging] = useState(false);

  const handleAcknowledge = useCallback(() => {
    if (isAcknowledging) return;
    setIsAcknowledging(true);

    // Store the pending ACK flag — PatientSessionPage will send DISCLAIMER_ACK
    // over the patient WebSocket once it connects (ARCH-009: server enforces
    // the gate; client sends the signal as soon as WS is ready).
    sessionStorage.setItem(DISCLAIMER_ACK_KEY, 'true');

    void navigate(`/session/${sessionId ?? ''}/patient`);
  }, [isAcknowledging, sessionId, navigate]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div
      dir="rtl"
      lang="ar"
      className="flex flex-col items-center gap-8 w-full"
      style={{ fontFamily: "'Source Sans 3', sans-serif" }}
    >
      {/* Card */}
      <div
        className="w-full bg-white rounded-clinical border border-clinical-border"
        style={{ padding: '32px 28px' }}
      >
        {/* Icon — info shield */}
        <div className="flex justify-center mb-6" aria-hidden="true">
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              width: '64px',
              height: '64px',
              background: 'rgba(13, 148, 136, 0.1)',
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-teal)"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              width={32}
              height={32}
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1
          className="font-heading font-semibold text-clinical-charcoal text-center mb-3"
          style={{ fontSize: '26px', lineHeight: '1.3' }}
        >
          {STRINGS.title}
        </h1>

        {/* Intro line */}
        <p
          className="text-clinical-text-muted text-center mb-6"
          style={{ fontSize: '18px' }}
        >
          {STRINGS.intro}
        </p>

        {/* Points list */}
        <ul
          className="flex flex-col gap-4"
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
          role="list"
        >
          {STRINGS.points.map((point, idx) => (
            <li
              key={idx}
              className="flex items-start gap-3"
              style={{ fontSize: '20px', lineHeight: '1.5' }}
            >
              {/* Tick icon */}
              <span
                className="flex-none mt-1 flex items-center justify-center rounded-full"
                style={{
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  background: 'rgba(13, 148, 136, 0.15)',
                }}
                aria-hidden="true"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-teal)"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width={14}
                  height={14}
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span className="text-clinical-charcoal">{point}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Acknowledge button — large, prominent, full-width */}
      <button
        type="button"
        onClick={handleAcknowledge}
        disabled={isAcknowledging}
        className="w-full rounded-clinical font-body font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{
          minHeight: '64px',
          fontSize: '22px',
          backgroundColor: 'var(--color-teal)',
        }}
        aria-busy={isAcknowledging}
      >
        {isAcknowledging ? STRINGS.acknowledging : STRINGS.ackButton}
      </button>
    </div>
  );
}
